import { INFLUENCE, type LeverId, type TradeGoodId } from '@sd/content';

/**
 * Economic levers and exchange events (GDD §6). Pure helpers; the match
 * drives them from `step`.
 */
export interface Lever {
  readonly id: number;
  readonly kind: LeverId;
  /** Who pulled it; for sanctions, every backer of the coalition. */
  readonly by: readonly string[];
  /** The player it is aimed at (embargo, sanctions), else null. */
  readonly target: string | null;
  /** The good it moves (dumping), else null. */
  readonly good: TradeGoodId | null;
  /** In force (or, while gathering backers, open) until this tick. */
  readonly until: number;
  /** Sanctions wait for a second backer; every other lever is active at once. */
  readonly status: 'gathering' | 'active';
}

/** A glut or a shortage announced a cycle before it hits the price. */
export interface MarketWarning {
  readonly good: TradeGoodId;
  readonly kind: 'glut' | 'shortage';
}

const active = (levers: readonly Lever[]) => levers.filter((l) => l.status === 'active');

/** Whether active sanctions are aimed at `player`. */
export const isSanctioned = (levers: readonly Lever[], player: string): boolean =>
  active(levers).some((l) => l.kind === 'sanctions' && l.target === player);

/** Whether `from` holds an active embargo against `target`. */
export const hasEmbargo = (levers: readonly Lever[], from: string, target: string): boolean =>
  active(levers).some((l) => l.kind === 'embargo' && l.target === target && l.by.includes(from));

/** Players with an active tariff. */
export const tariffOwners = (levers: readonly Lever[]): Set<string> =>
  new Set(active(levers).flatMap((l) => (l.kind === 'tariff' ? l.by : [])));

/**
 * Influence earned in one cycle: trade share, contract exports in ГС, and
 * the reserve status. The total is capped by the caller.
 */
export function influenceGain(share: number, exportsGs: number, isIssuer: boolean): number {
  return (
    INFLUENCE.perTradeShare * share +
    (INFLUENCE.perExportHundred * exportsGs) / 100 +
    (isIssuer ? INFLUENCE.reserveIssuer : 0)
  );
}

/**
 * Why a contract from `seller` to `buyer` cannot be signed or loaded right
 * now: sanctions freeze the target's contracts, an embargo stops the
 * embargoer's own deliveries to its target. Null when nothing stands in the way.
 */
export function contractBlock(
  levers: readonly Lever[],
  seller: string,
  buyer: string,
): 'sanctioned' | 'embargo' | null {
  if (isSanctioned(levers, seller) || isSanctioned(levers, buyer)) return 'sanctioned';
  return hasEmbargo(levers, seller, buyer) ? 'embargo' : null;
}
